import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import i18n from 'i18n';
import { Button } from 'react-bootstrap';
import { useCallback } from 'react';
import { defaultTo } from 'lodash';
import { Pmp } from 'core/pmp/Pmp';
import { DeliverType, RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import {
  PmpListColumns,
  usePmpListModel
} from 'containers/PmpOrder/PmpListModel';
import {
  PmpCampaign,
  PmpFormModel
} from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/PmpFormModel';

export const SelectPmpStep: React.FC<{
  pmpFormModel: PmpFormModel;
  goNext: (pmp: Pmp, campaign: PmpCampaign, createNewCreative: boolean) => void;
  goLast: () => void;
}> = ({ pmpFormModel, goNext, goLast }) => {
  const { pmp, pmpList, pmpSpaceOptions } = pmpFormModel;

  const { loading, columns, filterdPmps, selectedPmps, onHandleSelect } =
    usePmpListModel(
      defaultTo(pmpSpaceOptions, []),
      true,
      pmp ? [pmp.id] : [],
      pmpList,
      false,
      false,
      false,
      false
    );

  const completeAndCheck = useCallback(
    (createNewCreative: boolean) => {
      const pmp = filterdPmps.find((pmp) => pmp.id === selectedPmps[0]);
      if (!pmp) return;

      const bidPrice = 0.01;
      const campaignBasic = {
        name: pmp.name,
        budget: pmp.budget,
        startDate: pmp.startTime,
        endDate: pmp.endTime,
        priceModel: RtbCampaignPlanType.FCPM,
        optimize: L2ObjectOptimizationGoal.IMPRESSIONS,
        orderPrice: bidPrice,
        bidPrice: bidPrice,
        deliverType: DeliverType.ACCELERATED,
        bidStrategy: BidStrategy.LOWEST_COST_WITH_BID_CAP,
        dayPart: pmp.dayPart
      };
      goNext(
        pmp,
        {
          basic: campaignBasic,
          limitations: {
            exclude: [],
            preferred: [],
            nonPreferred: [],
            other: [
              {
                op: 'inc',
                type: 'dealId',
                value: [{ label: pmp.dealId, value: pmp.dealId }]
              }
            ],
            include: [
              {
                op: 'inc',
                type: 'adspace',
                value: pmp.spaces.map((space) => {
                  const spaceOption = pmpSpaceOptions.find(
                    (option) => option.value === space
                  );
                  return {
                    label: spaceOption ? spaceOption.label : space,
                    value: space
                  };
                })
              }
            ]
          }
        },
        createNewCreative
      );
    },
    [filterdPmps, selectedPmps, pmpSpaceOptions, goNext]
  );

  const goCreateCreativeStep = useCallback(() => {
    completeAndCheck(true);
  }, [completeAndCheck]);

  const goBindCreativeStep = useCallback(() => {
    completeAndCheck(false);
  }, [completeAndCheck]);

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      {loading && <LoadingIndicator />}
      <StickableTanstackTable
        customPadding={false}
        keyField={'id'}
        data={filterdPmps}
        columns={columns}
        noDataIndication={i18n.t('selectPmpStep.placeholders.noData')}
        defaultSorted={[
          {
            dataField: PmpListColumns.NAME,
            order: 'desc'
          }
        ]}
        selectedRows={selectedPmps}
        onSelect={onHandleSelect}
        hidePagination={true}
      />
      <div style={{ padding: '30px' }}>
        <Button
          disabled={selectedPmps.length === 0}
          style={{ marginRight: '20px' }}
          size='sm'
          onClick={goCreateCreativeStep}
        >
          {i18n.t('selectPmpStep.buttons.createNewCreative')}
        </Button>
        <Button
          disabled={selectedPmps.length === 0}
          style={{ marginRight: '20px' }}
          size='sm'
          onClick={goBindCreativeStep}
        >
          {i18n.t('selectPmpStep.buttons.bindCreative')}
        </Button>
        <Button variant='secondary' size='sm' onClick={goLast}>
          {i18n.t('common.buttons.back')}
        </Button>
      </div>
    </div>
  );
};
